import { jsx } from "react/jsx-runtime";
import { autoUpdate, offset, useFloating } from "@floating-ui/react";
import { useComposedRefs } from "@tamagui/compose-refs";
import { useIsomorphicLayoutEffect } from "@tamagui/core";
import { YStack } from "@tamagui/stacks";
import * as React from "react";
import { flushSync } from "react-dom";
import { useSelectContext } from "./context";
const SCROLL_UP_BUTTON_NAME = "SelectScrollUpButton";
const SelectScrollUpButton = React.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ jsx(
    SelectScrollButtonImpl,
    {
      componentName: SCROLL_UP_BUTTON_NAME,
      ...props,
      dir: "up",
      ref: forwardedRef
    }
  );
});
SelectScrollUpButton.displayName = SCROLL_UP_BUTTON_NAME;
const SCROLL_DOWN_BUTTON_NAME = "SelectScrollDownButton";
const SelectScrollDownButton = React.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ jsx(
    SelectScrollButtonImpl,
    {
      componentName: SCROLL_DOWN_BUTTON_NAME,
      ...props,
      dir: "down",
      ref: forwardedRef
    }
  );
});
SelectScrollDownButton.displayName = SCROLL_DOWN_BUTTON_NAME;
const SelectScrollButtonImpl = React.memo(
  React.forwardRef(
    (props, forwardedRef) => {
      var _a;
      const { __scopeSelect, dir, componentName, ...scrollIndicatorProps } = props;
      const {
        floatingRef,
        forceUpdate,
        open,
        fallback,
        setScrollTop,
        setInnerOffset,
        ...context
      } = useSelectContext(componentName, __scopeSelect);
      const [element, setElement] = React.useState(null);
      const statusRef = React.useRef("idle");
      const isVisible = context[dir === "down" ? "canScrollDown" : "canScrollUp"];
      const frameRef = React.useRef();
      const { x, y, reference, floating, strategy, update, refs } = useFloating({
        open: open && isVisible,
        strategy: "fixed",
        placement: dir === "up" ? "top" : "bottom",
        middleware: [offset(({ rects }) => -rects.floating.height)],
        whileElementsMounted: (...args) => autoUpdate(...args, { animationFrame: true })
      });
      const composedRef = useComposedRefs(forwardedRef, floating);
      if (floatingRef) {
        if (open) {
          if (element !== floatingRef.current) {
            setElement(floatingRef.current);
            reference(floatingRef.current);
            requestAnimationFrame(update);
          }
        } else {
          cancelAnimationFrame(frameRef.current);
        }
      }
      useIsomorphicLayoutEffect(() => {
        return () => {
          cancelAnimationFrame(frameRef.current);
        };
      }, []);
      if (!(isVisible && floatingRef)) {
        return null;
      }
      const onScroll = (amount) => {
        if (fallback) {
          if (refs.floating.current) {
            refs.floating.current.scrollTop -= amount;
            flushSync(() => {
              var _a2;
              return setScrollTop(((_a2 = refs.floating.current) == null ? void 0 : _a2.scrollTop) ?? 0);
            });
          }
        } else {
          flushSync(() => setInnerOffset((value) => value - amount));
        }
      };
      return /* @__PURE__ */ jsx(
        YStack,
        {
          ref: composedRef,
          componentName,
          "aria-hidden": true,
          ...scrollIndicatorProps,
          zIndex: 1e3,
          position: strategy,
          left: x || 0,
          top: y || 0,
          width: `calc(${(((_a = floatingRef == null ? void 0 : floatingRef.current) == null ? void 0 : _a.offsetWidth) ?? 0) - 2}px)`,
          onPointerEnter: () => {
            statusRef.current = "active";
            let prevNow = Date.now();
            function frame() {
              if (element) {
                const currentNow = Date.now();
                const msElapsed = currentNow - prevNow;
                prevNow = currentNow;
                const pixelsToScroll = msElapsed / 2;
                const remainingPixels = dir === "up" ? element.scrollTop : element.scrollHeight - element.clientHeight - element.scrollTop;
                const scrollRemaining = dir === "up" ? element.scrollTop - pixelsToScroll > 0 : element.scrollTop + pixelsToScroll < element.scrollHeight - element.clientHeight;
                onScroll(
                  dir === "up" ? Math.min(pixelsToScroll, remainingPixels) : Math.max(-pixelsToScroll, -remainingPixels)
                );
                if (scrollRemaining) {
                  frameRef.current = requestAnimationFrame(frame);
                }
              }
            }
            cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(frame);
          },
          onPointerLeave: () => {
            statusRef.current = "idle";
            cancelAnimationFrame(frameRef.current);
          }
        }
      );
    }
  )
);
export {
  SelectScrollDownButton,
  SelectScrollUpButton
};
//# sourceMappingURL=SelectScrollButton.js.map
